// src/features/recruitment/Candidate/AddCandidate.jsx
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { FaUser } from "react-icons/fa6";
import { IoIosAddCircleOutline } from "react-icons/io";
import config from "../../../config";
import React, { useEffect, useMemo, useState } from "react";
import { VscChevronRight } from "react-icons/vsc";
import SuccessPopup from "../../../components/common/SuccessPopup";
import ErrorPopup from "../../../components/common/ErrorPopup";
import { GrNotification } from "react-icons/gr";
import { IoIosArrowBack } from "react-icons/io";
import Header from "../../../components/common/Header";
// --- Auto-selection Helpers (Moved outside to prevent re-creation/remounting) ---

const normalizeStr = (str) => String(str || "").toLowerCase().replace(/[\s\-_]+/g, "");

const SubVendorAutoSelect = ({
  subVendors,
  selectedCandidate,
  setFieldValue,
}) => {
  useEffect(() => {
    if (selectedCandidate?.subVendorName && subVendors.length > 0) {
      const vendor = subVendors.find(
        (v) => normalizeStr(v.subVendorName || v.name) === normalizeStr(selectedCandidate.subVendorName)
      );

      if (vendor) {
        setFieldValue("subVendor", vendor.subVendorName || vendor.name);
      }
    }
  }, [subVendors, selectedCandidate, setFieldValue]);

  return null;
};

const LocationAutoSelect = ({
  countries,
  states,
  districts,
  setFieldValue,
  selectedCountry,
  selectedState,
  setSelectedCountry,
  setSelectedState,
  values,
}) => {
  // 🌍 Country Auto-select
  useEffect(() => {
    if (countries.length > 0) {
      const countryVal = normalizeStr(values.country);
      if (!countryVal) return;

      const found = countries.find(
        (c) =>
          normalizeStr(c.id || c.countryId) === countryVal ||
          normalizeStr(c.name || c.countryName) === countryVal ||
          normalizeStr(c.code) === countryVal
      );

      if (found) {
        const id = String(found.id || found.countryId);
        if (String(values.country) !== id) {
          setFieldValue("country", id);
        }
        if (String(selectedCountry) !== id) {
          setSelectedCountry(id);
        }
      }
    }
  }, [countries, setFieldValue, setSelectedCountry, values.country, selectedCountry]);

  // 🗺️ State Auto-select
  useEffect(() => {
    if (states.length > 0) {
      const stateVal = normalizeStr(values.state);
      if (!stateVal) return;

      const found = states.find(
        (s) =>
          normalizeStr(s.id || s.stateId) === stateVal ||
          normalizeStr(s.name || s.stateName) === stateVal ||
          normalizeStr(s.code) === stateVal
      );

      if (found) {
        const id = String(found.id || found.stateId);
        if (String(values.state) !== id) {
          setFieldValue("state", id);
        }
        if (String(selectedState) !== id) {
          setSelectedState(id);
        }
      }
    }
  }, [states, setFieldValue, setSelectedState, values.state, selectedState]);

  // 🏙️ District Auto-select
  useEffect(() => {
    if (districts.length > 0) {
      const districtVal = normalizeStr(values.district);
      if (!districtVal) return;

      const found = districts.find(
        (d) =>
          normalizeStr(d.id || d.districtId) === districtVal ||
          normalizeStr(d.name || d.districtName) === districtVal ||
          normalizeStr(d.code) === districtVal
      );

      if (found) {
        const id = String(found.id || found.districtId);
        if (String(values.district) !== id) {
          setFieldValue("district", id);
        }
      }
    }
  }, [districts, setFieldValue, values.district]);

  return null;
};

const AddOnboarding = ({
  onCancel,
  selectedCandidate,
  fromConvert,
  onDelete,
  onConvertToEmployee,
  onSave,
}) => {
  const isEditMode = !!selectedCandidate && selectedCandidate.id != null;
  const [isViewMode, setIsViewMode] = useState(isEditMode); // view mode when editing
  const [successPopup, setSuccessPopup] = useState(null);
  const [errorPopup, setErrorPopup] = useState(null);

  // API-driven dropdowns
  const [clients, setClients] = useState([]);
  const [subVendors, setSubVendors] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [qualifications, setQualifications] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");

  const [loadingClients, setLoadingClients] = useState(true);
  const [clientError, setClientError] = useState(null);

  useEffect(() => {
    if (fromConvert) {
      setIsViewMode(false);
    }
  }, [fromConvert]);

  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    token: "",
    orgId: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const role = localStorage.getItem("role")?.toUpperCase() || "GUEST";
    const orgId = localStorage.getItem("orgId") || "";
    setUserDetails({
      name: `${firstName} ${lastName}`.trim(),
      role,
      token,
      orgId,
    });
  }, []);

  // Fetch SubVendors
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    const fetchSubVendors = async () => {
      try {
        const url = `${config.API_BASE_URL}${config.SUBVENDOR_LIST_ENDPOINT}?orgKey=${userDetails.orgId}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch sub vendors`);
        }

        const data = await response.json();

        const vendors = Array.isArray(data?.body)
          ? data.body.map((v) => ({
            id: v.subVendorId,
            name: v.subVendorName,
          }))
          : [];

        setSubVendors(vendors);
      } catch (err) {
        console.error("SubVendor API Error:", err);
        setSubVendors([]);
      }
    };

    fetchSubVendors();
  }, [userDetails.token, userDetails.orgId]);

  const handleDelete = async () => {
    if (!selectedCandidate?.id) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this onboarding record?"
    );
    if (!confirmDelete) return;

    try {
      const response = await fetch(
        `${config.API_BASE_URL}${config.ONBOARDING_LIST_ENDPOINT}/${selectedCandidate.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Delete failed");
      }

      setSuccessPopup("Onboarding deleted successfully!");

      setTimeout(() => {
        onDelete && onDelete();
      }, 800);
    } catch (err) {
      console.error("Delete error:", err);
      setErrorPopup("Failed to delete onboarding record.");
    }
  };

  // --- Validation ---
  const validationSchema = Yup.object({
    candidateName: Yup.string()
      .max(100, "Candidate Name must be at most 100 characters")
      .required("Candidate Name is required"),

    aadharNumber: isEditMode
      ? Yup.string().matches(/^[0-9]{12}$/, "Aadhar must be exactly 12 digits").notRequired()
      : Yup.string().required("Aadhar Number is required.").matches(/^[0-9]{12}$/, "Aadhar must be exactly 12 digits"),

    uanNumber: isEditMode
      ? Yup.string().matches(/^[0-9]{12}$/, "UAN must be exactly 12 digits").notRequired()
      : Yup.string().required("UAN Number is required.").matches(/^[0-9]{12}$/, "UAN must be exactly 12 digits"),

    phoneNumber: Yup.string()
      .matches(/^\+91\s?\d{10}$/, "Use +91 XXXXXXXXXX format.")
      .required("Phone is required."),

    emailId: Yup.string()
      .email("Enter a valid email")
      .test(
        "no-uppercase",
        "Email should not contain uppercase letters.",
        (value) => !value || value === value.toLowerCase()
      )
      .required("Email is required."),

    dob: Yup.string()
      .required("Date of Birth is required.")
      .test(
        "dob-valid",
        "DOB cannot be in the future and candidate must be at least 18 years old",
        (val) => {
          if (!val) return false;
          const dob = new Date(val);
          const today = new Date();
          // Normalize times
          dob.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);
          if (dob > today) return false; // not in future
          // compute age
          let age = today.getFullYear() - dob.getFullYear();
          const m = today.getMonth() - dob.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            age--;
          }
          return age >= 18;
        }
      ),

    gender: Yup.string()
      .oneOf(["Male", "Female", "Other"])
      .required("Gender is required."),

    address: Yup.string()
      .required("Address Line is required.")
      .max(100, "Address cannot exceed 100 characters"),

    city: Yup.string()
      .max(50, "City must be at most 50 characters")
      .required("City is required."),

    district: Yup.string().required("District is required."),

    state: Yup.string().required("State is required."),

    pincode: Yup.string()
      .matches(/^[0-9]{6}$/, "Pincode must be exactly 6 digits")
      .required("Pincode is required."),

    country: Yup.string().required("Country is required."),

    client: Yup.string().required("Client Name is required."),

    interviewDate: isEditMode ? Yup.string().notRequired() : Yup.string().required("Interview date is required."),

    interviewTime: isEditMode
      ? Yup.string().matches(/^([01]\d|2[0-3]):[0-5]\d$/, "Time must be HH:mm (24-hour)").notRequired()
      : Yup.string().matches(/^([01]\d|2[0-3]):[0-5]\d$/, "Time must be HH:mm (24-hour)").required("Interview time is required."),

    jobRole: Yup.string().required("Job Role is required."),

    subVendor: Yup.string().required("Sub Vendor Name is required."),

    qualification: Yup.string().required("Qualification is required."),

    skills: Yup.string()
      .required("Skills are required.")
      .max(50, "Skills must be at most 50 characters"),

    pointOfContactInterview: Yup.string().required(
      "Point of Contact is required."
    ),

    jobStatus: fromConvert
      ? Yup.string().required("Job Status is required")
      : Yup.string().notRequired(),

    dateOfJoining: fromConvert
      ? Yup.date()
        .required("Date of Joining is required")
        .min(
          new Date(new Date().setHours(0, 0, 0, 0)),
          "Date of Joining cannot be in the past"
        )
      : Yup.date().notRequired(),

    pocRole: isEditMode
      ? Yup.string().max(21, "POC Role must be at most 21 characters").matches(/^[A-Za-z0-9 ]*$/, "Only letters, numbers and spaces allowed").notRequired()
      : Yup.string().required("POC Role is required.").max(21, "POC Role must be at most 21 characters").matches(/^[A-Za-z0-9 ]*$/, "Only letters, numbers and spaces allowed"),
  });

  // 🔹 Map selectedCandidate → form values
  const initialValues = useMemo(() => {
    const blankInitialValues = {
      candidateName: "",
      aadharNumber: "",
      uanNumber: "",
      emailId: "",
      phoneNumber: "+91 ",
      dob: "",
      gender: "",

      address: "",
      city: "",
      district: "",
      state: "",
      country: "",
      pincode: "",

      client: "",
      subVendor: "",

      recruitmentName: "",
      jobRole: "",
      skills: "",

      interviewDate: "",
      interviewTime: "",

      qualification: "",
      pointOfContactInterview: "",
      pocRole: "",

      jobStatus: fromConvert ? "ONBOARDED" : "",
      dateOfJoining: "",
    };

    if (!isEditMode || !selectedCandidate) return blankInitialValues;

    const c = selectedCandidate;
    const normalizedPhone =
      c.phone && c.phone.startsWith("+91")
        ? `+91 ${c.phone.replace("+91", "").trim()}`
        : c.phone || "+91 ";

    return {
      candidateName: c.name || "",
      aadharNumber: c.aadhar || "",
      uanNumber: c.uan || "",
      emailId: c.email || "",
      phoneNumber: normalizedPhone,
      dob: c.dob || "",
      gender: c.gender || "",
      address: c.addressLine1 || "",
      city: c.city || "",
      district: c.district || "",
      state: c.state || "",
      country: c.country || "",
      pincode: c.pinCode || "",
      client: c.clientName || "",
      recruitmentName: c.recruitmentId ? String(c.recruitmentId) : "",
      jobRole: c.jobRole || "",
      skills: c.skillsConfigured || "",
      subVendor: c.subVendorName || "",
      interviewDate: c.interviewDate || "",
      interviewTime: c.interviewTime
        ? c.interviewTime.slice(0, 5) // convert 10:30:00 → 10:30
        : "",
      qualification: c.qualification || "",
      pointOfContactInterview: c.pocName || "",
      pocRole: c.pocRole || "",
      jobStatus: c.jobStatus || "",
      dateOfJoining: c.dateOfJoining
        ? c.dateOfJoining.slice(0, 10) // if ISO datetime
        : "",
    };
  }, [isEditMode, selectedCandidate, fromConvert]);


  // --- Common dropdown fetcher (Config APIs) ---
  const fetchDropdownOptions = async (type, setter) => {
    try {
      const res = await fetch(
        `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${userDetails.orgId}?configType=${type}`,
        {
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("Failed to fetch " + type);
      const data = await res.json();
      const active = (Array.isArray(data) ? data : []).filter(
        (i) => i.isActive
      );

      if (setter === setJobRoles || setter === setQualifications) {
        const vals = active
          .map((i) => (typeof i.values === "string" ? i.values.trim() : ""))
          .filter(Boolean);
        setter([...new Set(vals)]);
      } else {
        setter(active);
      }
    } catch (e) {
      console.error(`Error fetching ${type}:`, e);
    }
  };

  const loadJobRoles = () => fetchDropdownOptions("JobRole", setJobRoles);
  const loadQualifications = () =>
    fetchDropdownOptions("Qualification", setQualifications);

  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;
    loadJobRoles();
    loadQualifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails.token, userDetails.orgId]);

  // Countries -> States -> Districts
  useEffect(() => {
    const run = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}${config.COUNTRY_ENDPOINT}`,
          {
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Failed to fetch countries");
        const data = await res.json();
        const countriesList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        setCountries(countriesList);
      } catch (e) {
        console.error(e);
      }
    };
    run();
  }, []);

  useEffect(() => {
    if (!selectedCountry) {
      setStates([]);
      return;
    }
    const run = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}${config.STATE_ENDPOINT}?countryId=${selectedCountry}`,
          {
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Failed to fetch states");
        const data = await res.json();
        const statesList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        setStates(statesList);
      } catch (e) {
        console.error(e);
      }
    };
    run();
  }, [selectedCountry]);

  useEffect(() => {
    if (!selectedState) {
      setDistricts([]);
      return;
    }
    const run = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}${config.DISTRICT_ENDPOINT}?stateId=${selectedState}`,
          {
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Failed to fetch districts");
        const data = await res.json();
        const districtsList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        setDistricts(districtsList);
      } catch (e) {
        console.error(e);
      }
    };
    run();
  }, [selectedState]);

  // ---- Fetch CLIENTS ----
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const response = await fetch(
          `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${userDetails.orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${userDetails.token}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch clients");
        }

        const data = await response.json();

        console.log("Client API response:", data);

        const clientsList = Array.isArray(data?.body)
          ? data.body.map((c) => ({
            id: c.clientKey, // unique key
            name: c.name, // dropdown text
          }))
          : [];

        setClients(clientsList);
        setClientError(null);
      } catch (err) {
        console.error("Client fetch error:", err);
        setClientError("Could not load clients.");
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [userDetails.token, userDetails.orgId]);

  // ---- Fetch RECRUITMENTS ----
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    const fetchRecruitments = async () => {
      try {
        const url = `${config.API_BASE_URL}${config.RECRUITMENT_LIST_ENDPOINT}?orgKey=${userDetails.orgId}`;
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        if (!res.ok)
          throw new Error(`Failed to fetch recruitments (HTTP ${res.status})`);
        // const data = await res.json();
        // ... (data not used currently)
      } catch (e) {
        console.error("Recruitment fetch error:", e);
      }
    };

    fetchRecruitments();
  }, [userDetails.token, userDetails.orgId]);

  const handleSubmit = async (values, { setSubmitting }) => {
    console.log("handleSubmit called with values:", values);
    try {
      const isUpdating = isEditMode && !fromConvert;

      // 1. Ensure all numeric strings stay as strings if the DB expects VARCHAR
      // 2. Ensure date formats are YYYY-MM-DD
      const payload = {
        id: selectedCandidate?.id || null,
        candidateKey: selectedCandidate?.candidateKey || "",   // ✅ ADD THIS
        orgKey: userDetails.orgId,

        candidateName: values.candidateName,
        aadharNumber: String(values.aadharNumber),
        uanNumber: String(values.uanNumber),
        email: values.emailId,
        phoneNumber: values.phoneNumber.replace(/\s+/g, ""),
        dateOfBirth: values.dob,
        gender: values.gender,
        client: values.client,
        subVendor: values.subVendor,
        interviewDate: values.interviewDate,
        interviewTime:
          values.interviewTime.length === 5
            ? `${values.interviewTime}:00`
            : values.interviewTime,
        jobRole: values.jobRole,
        qualification: values.qualification,
        skills: values.skills,
        pointOfContact: values.pointOfContactInterview,
        jobStatus: values.jobStatus || "ONBOARDED",
        dateOfJoining: values.dateOfJoining,
        address: values.address,
        city: values.city || "Madurai",
        district: values.district,
        state: values.state,
        country: values.country,
        pincode: values.pincode,
      };

      const url = isUpdating
        ? `${config.API_BASE_URL}${config.ONBOARDING_VIEW_LIST_ENDPOINT}/${selectedCandidate.id}`
        : `${config.API_BASE_URL}${config.ONBOARDING_VIEW_LIST_ENDPOINT}`;

      const method = isUpdating ? "PUT" : "POST";

      console.log("isEditMode:", isEditMode);
      console.log("fromConvert:", fromConvert);
      console.log("Updating?", isUpdating);
      console.log("URL:", url);
      console.log("Method:", method);

      console.log("Calling API:", method, url);

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Operation failed");
      }

      setSuccessPopup(
        isUpdating
          ? "Onboarding updated successfully!"
          : "Candidate successfully converted to Onboarding!"
      );
    } catch (err) {
      console.error("Submit error:", err);
      setErrorPopup(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const isReadOnly = isViewMode;
  return (
    <div>
      {successPopup && (
        <SuccessPopup
          message={successPopup}
          onClose={() => {
            setSuccessPopup(null);
            if (onSave) {
              onSave();
            } else {
              onCancel && onCancel();
            }
          }}
        />
      )}
      {errorPopup && (
        <ErrorPopup message={errorPopup} onClose={() => setErrorPopup(null)} />
      )}

      {/* Header */}
      <Header
        showBack={true}
        onBack={onCancel}
        title={
          fromConvert
            ? "Convert to Onboarding"
            : isViewMode
              ? "View Onboarding"
              : isEditMode
                ? "Edit Onboarding"
                : "Add Onboarding"
        }
        subtitle={
          <>
            All Onboarding <VscChevronRight style={{ verticalAlign: "middle" }} />{" "}
            {fromConvert
              ? "Convert to Onboarding"
              : isViewMode
                ? "View Onboarding"
                : isEditMode
                  ? "Edit Onboarding"
                  : "Add Onboarding"}
          </>
        }
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue, values, errors }) => {
          if (Object.keys(errors).length > 0) {
            console.log("Formik Validation Errors:", errors);
          }
          return (
            <>
              <SubVendorAutoSelect
                subVendors={subVendors}
                selectedCandidate={selectedCandidate}
                setFieldValue={setFieldValue}
              />
              <LocationAutoSelect
                countries={countries}
                states={states}
                districts={districts}
                setFieldValue={setFieldValue}
                selectedCountry={selectedCountry}
                selectedState={selectedState}
                setSelectedCountry={setSelectedCountry}
                setSelectedState={setSelectedState}
                values={values}
              />

              <Form className='add-client-form'>
                {isEditMode && !fromConvert && (
                  <div className='client-form-action-bar'>
                    <button
                      type='button'
                      className='convert-employee-btn'
                      onClick={() =>
                        onConvertToEmployee &&
                        onConvertToEmployee(selectedCandidate)
                      }
                    >
                      Convert to Employee
                    </button>
                  </div>
                )}

                {/* Candidate Information */}
                <section className='form-section'>
                  <h3>
                    <span className='icon'>
                      <FaUser />
                    </span>{" "}
                    Candidate Information
                  </h3>
                  <div className='form-grid'>
                    <div className='input-group'>
                      <label>
                        Candidate Name{" "}
                        <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        type='text'
                        name='candidateName'
                        disabled={isReadOnly}
                      />
                      <ErrorMessage
                        name='candidateName'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Aadhar Number <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        type='text'
                        name='aadharNumber'
                        disabled={isReadOnly}
                      />
                      <ErrorMessage
                        name='aadharNumber'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        UAN Number <span className='required-asterisk'>*</span>
                      </label>
                      <Field type='text' name='uanNumber' disabled={isReadOnly} />
                      <ErrorMessage
                        name='uanNumber'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Email Address <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        type='email'
                        name='emailId'
                        disabled={isReadOnly}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          setFieldValue("emailId", e.target.value.toLowerCase());
                        }}
                      />
                      <ErrorMessage
                        name='emailId'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Phone Number <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        type='text'
                        name='phoneNumber'
                        disabled={isReadOnly}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          let value = e.target.value;
                          if (!value.startsWith("+91")) {
                            value = value.replace(/^\+?91\s?/, "");
                            value =
                              "+91 " + value.replace(/\D/g, "").slice(0, 10);
                          } else {
                            value =
                              "+91 " +
                              value
                                .replace("+91", "")
                                .trim()
                                .replace(/\D/g, "")
                                .slice(0, 10);
                          }
                          setFieldValue("phoneNumber", value);
                        }}
                      />
                      <ErrorMessage
                        name='phoneNumber'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-row'>
                      <div className='input-group half-width'>
                        <label>
                          Date Of Birth
                          <span className='required-asterisk'>*</span>
                        </label>
                        <Field type='date' name='dob' disabled={isReadOnly} />
                        <ErrorMessage
                          name='dob'
                          className='error'
                          component='div'
                        />
                      </div>
                    </div>

                    <div className='input-group'>
                      <label>
                        Gender <span className='required-asterisk'>*</span>
                      </label>
                      <Field as='select' name='gender' disabled={isReadOnly}>
                        <option value=''></option>
                        <option value='Male'>Male</option>
                        <option value='Female'>Female</option>
                        <option value='Other'>Other</option>
                      </Field>
                      <ErrorMessage
                        name='gender'
                        component='div'
                        className='error'
                      />
                    </div>
                  </div>
                </section>

                {/* Job Information */}
                <section className='form-section'>
                  <h3>
                    <span className='icon'>
                      <FaUser />
                    </span>
                    <span>Job Information</span>
                    <span className='add-icon'>
                      <IoIosAddCircleOutline />
                    </span>
                  </h3>
                  <div className='form-grid'>
                    <div className='input-group'>
                      <label>
                        Client <span className='required-asterisk'>*</span>
                      </label>

                      <Field
                        as='select'
                        name='client'
                        disabled={isReadOnly || loadingClients || !!clientError}
                      >
                        <option value=''>
                          {loadingClients
                            ? "Loading clients..."
                            : clientError
                              ? "Failed to load clients"
                              : "Select Client"}
                        </option>

                        {clients.map((c) => (
                          <option key={c.id} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                      </Field>

                      <ErrorMessage
                        name='client'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Sub Vendor <span className='required-asterisk'>*</span>
                      </label>
                      <Field as='select' name='subVendor' disabled={isReadOnly}>
                        <option value=''>Select Sub Vendor</option>
                        {subVendors.map((vendor) => (
                          <option key={vendor.id} value={vendor.name}>
                            {vendor.name}
                          </option>
                        ))}
                      </Field>

                      <ErrorMessage
                        name='subVendor'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Interview Date
                        <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        type='date'
                        name='interviewDate'
                        disabled={isReadOnly}
                      />
                      <ErrorMessage
                        name='interviewDate'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Interview Time
                        <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        type='time'
                        name='interviewTime'
                        disabled={isReadOnly}
                      />
                      <ErrorMessage
                        name='interviewTime'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Job Role <span className='required-asterisk'>*</span>
                      </label>
                      <Field as='select' name='jobRole' disabled={isReadOnly}>
                        <option value=''></option>
                        {jobRoles.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name='jobRole'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Qualification <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        as='select'
                        name='qualification'
                        disabled={isReadOnly}
                      >
                        <option value=''></option>
                        {qualifications.map((q) => (
                          <option key={q} value={q}>
                            {q}
                          </option>
                        ))}
                      </Field>
                    </div>

                    <div className='input-group'>
                      <label>
                        Skills <span className='required-asterisk'>*</span>
                      </label>
                      <Field type='text' name='skills' disabled={isReadOnly} />
                      <ErrorMessage
                        name='skills'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Point of Contact / Interviewer
                        <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        type='text'
                        name='pointOfContactInterview'
                        disabled={isReadOnly}
                      />
                      <ErrorMessage
                        name='pointOfContactInterview'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Job Status <span className='required-asterisk'>*</span>
                      </label>

                      <Field
                        as='select'
                        name='jobStatus'
                        disabled={isReadOnly}
                      >
                        <option value=''>Select Job Status</option>
                        <option value='ONBOARDED'>ONBOARDED</option>
                        <option value='CANCELLED'>CANCELLED</option>
                      </Field>

                      <ErrorMessage
                        name='jobStatus'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-row'>
                      <div className='input-group half-width'>
                        <label>
                          Date Of Joining
                          <span className='required-asterisk'>*</span>
                        </label>
                        <Field
                          type='date'
                          name='dateOfJoining'
                          disabled={isReadOnly}
                          min={new Date().toISOString().split("T")[0]}
                        />
                        <ErrorMessage
                          name='dateOfJoining'
                          className='error'
                          component='div'
                        />
                      </div>
                    </div>
                  </div>
                </section>

                {/* Address Information */}
                <section className='form-section'>
                  <h3>
                    <span className='icon'>
                      <FaUser />
                    </span>{" "}
                    Address Information
                  </h3>
                  <div className='form-grid'>
                    <div className='input-group'>
                      <label>
                        Country <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        as='select'
                        name='country'
                        disabled={isReadOnly}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          const countryId = e.target.value;
                          setFieldValue("country", countryId);
                          setSelectedCountry(countryId);
                          setFieldValue("state", "");
                          setFieldValue("district", "");
                          setStates([]);
                          setDistricts([]);
                        }}
                      >
                        <option value=''>Select Country</option>
                        {countries.map((c) => (
                          <option key={c.id || c.countryId} value={c.id || c.countryId}>
                            {c.countryName || c.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name='country'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        State <span className='required-asterisk'>*</span>
                      </label>
                      <Field
                        as='select'
                        name='state'
                        disabled={isReadOnly}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          const stateId = e.target.value;
                          setFieldValue("state", stateId);
                          setSelectedState(stateId);
                          setFieldValue("district", "");
                          setDistricts([]);
                        }}
                      >
                        <option value=''>Select State</option>
                        {states.map((s) => (
                          <option key={s.id || s.stateId} value={s.id || s.stateId}>
                            {s.countryName || s.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name='state'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        District <span className='required-asterisk'>*</span>
                      </label>
                      <Field as='select' name='district' disabled={isReadOnly}>
                        <option value=''>Select District</option>
                        {districts.map((d) => (
                          <option key={d.id || d.districtId} value={d.id || d.districtId}>
                            {d.countryName || d.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name='district'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        City <span className='required-asterisk'>*</span>
                      </label>
                      <Field type='text' name='city' disabled={isReadOnly} />
                      <ErrorMessage
                        name='city'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Address Line / Street
                        <span className='required-asterisk'>*</span>
                      </label>
                      <Field type='text' name='address' disabled={isReadOnly} />
                      <ErrorMessage
                        name='address'
                        component='div'
                        className='error'
                      />
                    </div>

                    <div className='input-group'>
                      <label>
                        Pincode <span className='required-asterisk'>*</span>
                      </label>
                      <Field type='text' name='pincode' disabled={isReadOnly} />
                      <ErrorMessage
                        name='pincode'
                        component='div'
                        className='error'
                      />
                    </div>
                  </div>
                </section>

                {/* Actions */}
                <div className='button-container'>
                  {/* LEFT BUTTON */}
                  <button type='button' className='cancel-btn' onClick={onCancel}>
                    {fromConvert || isViewMode ? "Back" : "Cancel"}
                  </button>

                  {/* RIGHT BUTTON */}
                  {!isViewMode && (
                    <button
                      type='submit'
                      disabled={isSubmitting}
                      className='save-btn'
                    >
                      {isSubmitting
                        ? fromConvert
                          ? "Submitting..."
                          : isEditMode
                            ? "Updating..."
                            : "Saving..."
                        : fromConvert
                          ? "Submit"
                          : isEditMode
                            ? "Update"
                            : "Add"}
                    </button>
                  )}
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default AddOnboarding;
